import React, { useState, useEffect, useRef } from 'react';
import { Court, ROLE_META, ROLE_ORDER } from './DropInCourt';
import InfoIcon from './InfoIcon';
import './DropInFlow.css';
import { useAuth } from '../AuthContext';
import { useAuthModal } from '../AuthModalContext';
import { supabase } from '../supabaseClient';
import { useCart } from '../CartContext';

/* ---------- Data ---------- */
const LEVELS = [
  {
    id: 'beg', num: '01', tag: 'Beginner',
    name: 'BEGINNER',
    desc: 'New to indoor volleyball or coming back to it. Casual rallies, no rotations enforced.',
    perGame: '6 / court',
    duration: '120 min',
  },
  {
    id: 'int', num: '02', tag: 'Intermediate',
    name: 'INTERMEDIATE',
    desc: 'Comfortable with all six skills. Plays a structured 5-1 with set positions and rotation.',
    perGame: '7 / court',
    duration: '120 min',
  },
  {
    id: 'adv', num: '03', tag: 'Advanced',
    name: 'ADVANCED',
    desc: 'Competitive 5-1, fast tempo, set plays. Confident hitters and consistent passers only.',
    perGame: '7 / court',
    duration: '120 min',
  },
];


const TEAMS = ['A', 'B', 'C', 'D'];
const TEAM_CAP = 7; // non-beg: S(1)+OH(2)+MB(2)+OPP(1)+L(1)
const TEAM_CAP_BEG = 6; // beg: open-play slots BEG1–BEG6
function teamCap(level) { return level === 'beg' ? TEAM_CAP_BEG : TEAM_CAP; }

function emptyClaims() {
  return { S: [], OH: [], MB: [], OPP: [], L: [] };
}

// eslint-disable-next-line no-unused-vars
function capacityFor(level, session) {
  // Total session capacity = 4 teams × 6 players
  if (level === 'beg') {
    const taken = (session.preClaims || []).length;
    return { taken, total: teamCap(level) * 4 };
  }
  const c = session.preClaims || emptyClaims();
  const taken = ROLE_ORDER.reduce((sum, r) => sum + (c[r]?.length || 0), 0);
  return { taken, total: teamCap(level) * 4 };
}

// Maps any stored position string (new code "S"/"OH1", legacy long name "Setter",
// or partial "OH"/"MB") to the claims-bucket key (S, OH, MB, OPP, L).
const POSITION_NAME_TO_KEY = {
  'setter': 'S', 's': 'S',
  'outside hitter': 'OH', 'oh': 'OH', 'oh1': 'OH', 'oh2': 'OH',
  'middle blocker': 'MB', 'mb': 'MB', 'mb1': 'MB', 'mb2': 'MB',
  'opposite': 'OPP', 'opp': 'OPP',
  'libero': 'L', 'l': 'L',
};
function normalizePositionKey(raw) {
  if (!raw) return null;
  return POSITION_NAME_TO_KEY[String(raw).toLowerCase().trim()] || null;
}

function buildClaimsFromRows(level, rows, currentUserId) {
  if (level === 'beg') {
    return (rows || []).map(r => {
      const num = (r.position || '').replace(/\D/g, '') || '?';
      return { slot: `BEG${num}`, userId: r.user_id || null, isYou: r.user_id === currentUserId };
    });
  }
  const claims = emptyClaims();
  (rows || []).forEach(r => {
    const key = normalizePositionKey(r.position);
    if (key && claims[key] !== undefined) {
      claims[key].push({ userId: r.user_id || null, isYou: r.user_id === currentUserId });
      return;
    }
    if (r.position && claims[r.position] !== undefined) {
      claims[r.position].push({ userId: r.user_id || null, isYou: r.user_id === currentUserId });
    }
  });
  return claims;
}

function findYourClaim(claims) {
  for (const r of ROLE_ORDER) {
    const arr = claims[r] || [];
    const you = arr.find(c => c.isYou);
    if (you) return { role: r };
  }
  return null;
}

/* ---------- Step bar ---------- */
function StepBar({ step }) {
  if (step === 'done') return null;
  const order = ['level', 'session', 'team', 'position', 'confirm'];
  const idx = order.indexOf(step);
  const labels = ['LEVEL', 'SESSION', 'TEAM', 'POSITION', 'CONFIRM'];
  return (
    <div>
      <div className="di-step-bar">
        {order.map((_, i) => (
          <div key={i} className={`di-step-pill ${i < idx ? 'done' : i === idx ? 'active' : ''}`} />
        ))}
      </div>
      <div className="di-step-meta">
        <span>STEP {idx + 1} / 5</span>
        <span>{labels[idx]}</span>
      </div>
    </div>
  );
}

/* ---------- Level step ---------- */
function LevelStep({ value, onRequestLevel }) {
  return (
    <div className="di-step">
      <div className="di-eyebrow">Drop-in Sign Up</div>
      <h1 className="di-title">CHOOSE YOUR <em>LEVEL</em></h1>
      <p className="di-sub">Pick the night that matches where you're at. Beginner is open play; Intermediate and Advanced are structured 5-1.</p>

      <div className="di-levels">
        {LEVELS.map(L => (
          <button key={L.id}
            className={`di-level-card lvl-${L.id} ${value === L.id ? 'selected' : ''}`}
            onClick={() => onRequestLevel(L.id)}>
            <div className="di-level-num">{L.num} / 03</div>
            <div className="di-level-tag"><span className="dot"></span>{L.tag.toUpperCase()}</div>
            <div className="di-level-name">{L.name}</div>
            <div className="di-level-desc">{L.desc}</div>
            <div className="di-level-stats">
              <div><span>Per court</span><b>{L.perGame}</b></div>
              <div><span>Length</span><b>{L.duration}</b></div>
              <div><span>Style</span><b>{L.id === 'beg' ? 'Open' : '5-1'}</b></div>
            </div>
          </button>
        ))}
      </div>

      <div className="di-channel-cta">
        <div className="di-channel-cta__icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
          </svg>
        </div>
        <div className="di-channel-cta__body">
          <p className="di-channel-cta__title">Never Miss a Drop-In</p>
          <p className="di-channel-cta__text">Runs fill fast. Join the OVR volleyball channel and be the first to know when new drop-ins go live.</p>
        </div>
        <a
          href="https://www.instagram.com/channel/AbaPFZyVLCMB9peZ/"
          target="_blank"
          rel="noopener noreferrer"
          className="di-channel-cta__btn"
        >
          Join the Volleyball Channel ↗
        </a>
      </div>
    </div>
  );
}

/* ---------- 5-1 confirm modal ---------- */
function FiveOneModal({ open, levelId, onConfirm, onCancel }) {
  const [dontAsk, setDontAsk] = useState(false);
  useEffect(() => { if (open) setDontAsk(false); }, [open]);
  if (!open) return null;
  const levelName = LEVELS.find(L => L.id === levelId)?.tag || '';
  return (
    <div className="di-modal-backdrop" onClick={onCancel}>
      <div className="di-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="di-modal-eyebrow">Quick check · {levelName}</div>
        <h2 className="di-modal-title">COMFORTABLE WITH<br/>A <em>5-1 ROTATION?</em></h2>
        <p>Our {levelName.toLowerCase()} drop-in runs a structured 5-1: one setter, two outsides, two middles, an opposite, and a libero. Players rotate clockwise after every side-out.</p>
        <ul className="di-modal-list">
          <li>You know which spot you're rotating into without being told.</li>
          <li>You can call your position and switch on the fly.</li>
          <li>You're confident with all six skills at game speed.</li>
        </ul>
        <label className="di-modal-check">
          <input type="checkbox" checked={dontAsk} onChange={e => setDontAsk(e.target.checked)} />
          <span className="box"></span>
          <span>Don't ask me again on this device</span>
        </label>
        <div className="di-modal-actions">
          <button className="di-btn ghost" onClick={onCancel}>NO, BACK TO MENU</button>
          <button className="di-btn primary" onClick={() => onConfirm(dontAsk)}>YES, I'M GOOD →</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Session step ---------- */
function SessionStep({ level, value, onChange, allSessions }) {
  const sessions = (allSessions || []).filter(s => s.level === level);
  const levelMeta = LEVELS.find(L => L.id === level);
  const [liveData, setLiveData] = useState({}); // { sessionId: { taken, teamsFull } }
  const [waitlistFor, setWaitlistFor] = useState(null); // session obj when modal open

  useEffect(() => {
    const ids = sessions.map(s => s.id);
    if (ids.length === 0) return;
    supabase
      .from('drop_in_registrations')
      .select('session_id, team_letter')
      .in('session_id', ids)
      .not('payment_status', 'in', '(admin_removed,refunded,user_cancelled)')
      .then(({ data }) => {
        const acc = {};
        ids.forEach(id => { acc[id] = { taken: 0, perTeam: { A:0, B:0, C:0, D:0 } }; });
        (data || []).forEach(r => {
          if (!acc[r.session_id]) return;
          acc[r.session_id].taken += 1;
          if (r.team_letter && acc[r.session_id].perTeam[r.team_letter] !== undefined) {
            acc[r.session_id].perTeam[r.team_letter] += 1;
          }
        });
        const out = {};
        Object.entries(acc).forEach(([id, v]) => {
          const teamsFull = Object.values(v.perTeam).filter(n => n >= teamCap(level)).length;
          out[id] = { taken: v.taken, teamsFull };
        });
        setLiveData(out);
      });
  }, [level]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="di-step">
      <div className="di-eyebrow">{levelMeta.tag} Drop-in</div>
      <h1 className="di-title">{sessions.length === 0 ? <>CHECK BACK <em>LATER</em></> : <>PICK A <em>DATE</em></>}</h1>
      {sessions.length > 0 && <p className="di-sub">Drop-ins run Wednesday and Thursday nights. Each session is 2 hours. Show up 10 minutes early to claim warm-up courts.</p>}

      {sessions.length === 0 && (
        <div style={{
          marginTop: 52,
          padding: '48px 32px',
          background: 'var(--di-surface)',
          border: '1px solid var(--di-border)',
          borderTop: '3px solid var(--di-accent)',
          textAlign: 'center',
        }}>
          <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 36, color: '#fff', letterSpacing: '0.08em', marginBottom: 10 }}>
            NO DATES <em style={{ color: 'var(--di-accent)', fontStyle: 'italic' }}>YET</em>
          </div>
          <div style={{ color: 'var(--di-text-dim)', fontSize: 14, maxWidth: 480, margin: '0 auto', lineHeight: 1.5 }}>
            We're locking in gym bookings now. Check back soon — {levelMeta.tag.toLowerCase()} drop-in dates are coming.
          </div>
        </div>
      )}

      <div className="di-sessions">
        {sessions.map(s => {
          const total = teamCap(level) * 4;
          const taken = liveData[s.id]?.taken ?? 0;
          const teamsFull = liveData[s.id]?.teamsFull ?? 0;
          const remaining = total - taken;
          const full = remaining <= 0;
          const cls = remaining === 0 ? 'full' : remaining <= 2 ? 'crit' : remaining <= 4 ? 'warn' : '';
          // When full we render a div container holding the row layout + SOLD OUT overlay
          // + waitlist button, so the button is cemented inside the card.
          if (full) {
            return (
              <div key={s.id} className="di-session-row full" style={{ pointerEvents: 'auto' }}>
                <div className="di-session-date">
                  <small>{s.day}</small>
                  {s.date}
                </div>
                <div className="di-session-info">
                  <div className="where">{s.location}</div>
                  <div className="meta">{s.address}</div>
                </div>
                <div className="di-session-time">{s.time}</div>
                <div className={`di-capacity-chip full`}>
                  FULL
                  <span style={{opacity:0.5}}>· {taken}/{total}</span>
                  <span style={{opacity:0.5}}>· {teamsFull}/4 TEAMS FULL</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setWaitlistFor(s); }}
                  style={{
                    position: 'absolute',
                    bottom: 14, right: 16, zIndex: 3,
                    background: '#fff', color: '#9D1515',
                    border: '2px solid #fff',
                    fontFamily: "'Bebas Neue','Oswald',sans-serif",
                    fontSize: 13, letterSpacing: '0.16em',
                    padding: '8px 16px', cursor: 'pointer',
                    boxShadow: '0 4px 18px rgba(0,0,0,0.55)',
                    pointerEvents: 'auto',
                  }}
                >
                  + JOIN WAITLIST
                </button>
              </div>
            );
          }
          return (
            <button key={s.id}
              className={`di-session-row ${value === s.id ? 'selected' : ''}`}
              onClick={() => onChange(s.id)}>
              <div className="di-session-date">
                <small>{s.day}</small>
                {s.date}
              </div>
              <div className="di-session-info">
                <div className="where">{s.location}</div>
                <div className="meta">{s.address}</div>
              </div>
              <div className="di-session-time">{s.time}</div>
              <div className={`di-capacity-chip ${cls}`}>
                {`${remaining} OPEN`}
                <span style={{opacity:0.5}}>· {taken}/{total}</span>
                <span style={{opacity:0.5}}>· {teamsFull}/4 TEAMS FULL</span>
              </div>
            </button>
          );
        })}
      </div>
      <WaitlistModal session={waitlistFor} onClose={() => setWaitlistFor(null)} />
    </div>
  );
}

function WaitlistModal({ session, onClose }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState(user?.user_metadata?.full_name || '');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('idle');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (session && user) {
      setEmail(user.email || '');
      setName(user.user_metadata?.full_name || '');
    }
  }, [session, user]);

  if (!session) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) {
      setStatus('error'); setErr('Name and email are required.'); return;
    }
    setStatus('sending'); setErr('');
    const { error } = await supabase.from('drop_in_waitlist').insert({
      session_id: session.id,
      session_label: session.month,
      user_id: user?.id || null,
      name: name.trim().slice(0, 120),
      email: email.trim().slice(0, 200),
      phone: phone.replace(/\D/g, '').slice(0, 20),
    });
    if (error && error.code !== '23505') {
      setStatus('error'); setErr('Could not join waitlist. Try again or DM us on Instagram.');
      return;
    }
    setStatus('sent');
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#0e0e0e', border: '1px solid #2a2a2a', borderLeft: '3px solid #9D1515',
        padding: 28, maxWidth: 460, width: '100%', color: '#fff',
      }}>
        <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 24, letterSpacing: '0.08em', marginBottom: 6 }}>
          JOIN WAITLIST
        </div>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: '0 0 18px', lineHeight: 1.55 }}>
          <strong style={{ color: '#fff' }}>{session.month}</strong> · {session.time} · {session.location}
        </p>
        {status === 'sent' ? (
          <div>
            <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.35)', borderLeft: '3px solid #4ade80', padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", color: '#4ade80', fontSize: 16, letterSpacing: '0.08em', marginBottom: 4 }}>
                YOU'RE ON THE LIST
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>
                We'll email you the moment a spot opens up. First come first served.
              </div>
            </div>
            <button onClick={onClose} style={{
              background: '#9D1515', color: '#fff', border: 'none',
              fontFamily: "'Bebas Neue','Oswald',sans-serif",
              fontSize: 12, letterSpacing: '0.14em',
              padding: '10px 18px', cursor: 'pointer', width: '100%',
            }}>GOT IT</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              placeholder="Your name"
              value={name} onChange={e => setName(e.target.value)} maxLength={120}
              style={{ background: '#0a0a0a', border: '1px solid #2a2a2a', color: '#fff', padding: '10px 12px', fontSize: 13, fontFamily: 'Inter, sans-serif', outline: 'none' }}
            />
            <input
              type="email" placeholder="your@email.com"
              value={email} onChange={e => setEmail(e.target.value)} maxLength={200}
              style={{ background: '#0a0a0a', border: '1px solid #2a2a2a', color: '#fff', padding: '10px 12px', fontSize: 13, fontFamily: 'Inter, sans-serif', outline: 'none' }}
            />
            <input
              type="tel" placeholder="Phone (optional)"
              value={phone} onChange={e => setPhone(e.target.value)} maxLength={20}
              style={{ background: '#0a0a0a', border: '1px solid #2a2a2a', color: '#fff', padding: '10px 12px', fontSize: 13, fontFamily: 'Inter, sans-serif', outline: 'none' }}
            />
            {err && <div style={{ fontSize: 12, color: '#ff6b6b' }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button type="button" onClick={onClose} style={{
                flex: 1, background: 'transparent', border: '1px solid #2a2a2a', color: '#aaa',
                fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.14em',
                padding: '10px 14px', cursor: 'pointer',
              }}>CANCEL</button>
              <button type="submit" disabled={status === 'sending'} style={{
                flex: 2, background: '#9D1515', color: '#fff', border: 'none',
                fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.14em',
                padding: '10px 14px', cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                opacity: status === 'sending' ? 0.6 : 1,
              }}>{status === 'sending' ? 'SAVING…' : 'JOIN WAITLIST →'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------- Team step ---------- */
function TeamStep({ level, session, value, onSelect, refreshKey }) {
  const [teamData, setTeamData] = useState(null);

  useEffect(() => {
    setTeamData(null);
    supabase
      .from('drop_in_registrations')
      .select('team_letter, position')
      .eq('session_id', session.id)
      .not('payment_status', 'in', '(admin_removed,refunded,user_cancelled)')
      .then(({ data }) => {
        const teams = { A: [], B: [], C: [], D: [] };
        (data || []).forEach(r => {
          if (r.team_letter && teams[r.team_letter]) {
            const key = normalizePositionKey(r.position) || r.position || '?';
            teams[r.team_letter].push(key);
          }
        });
        setTeamData(teams);
      });
  }, [session.id, refreshKey]);

  const levelMeta = LEVELS.find(L => L.id === level);
  const POS_CAPS = { S: 1, OH: 2, MB: 2, OPP: 1 };

  return (
    <div className="di-step">
      <div className="di-eyebrow">{levelMeta.tag} · {session.month}</div>
      <h1 className="di-title">PICK YOUR <em>TEAM</em></h1>
      <p className="di-sub">4 teams, {teamCap(level)} spots each. First come first served - choose a team, then claim your position.</p>

      <div className="di-team-grid">
        {TEAMS.map(letter => {
          const positions = teamData ? teamData[letter] : null;
          const count = positions ? positions.length : null;
          const full = count !== null && count >= teamCap(level);
          const selected = value === letter;

          const posCounts = level !== 'beg' && positions
            ? positions.reduce((acc, p) => { acc[p] = (acc[p] || 0) + 1; return acc; }, {})
            : null;

          return (
            <button key={letter}
              className={`di-team-card${selected ? ' selected' : ''}${full ? ' full' : ''}`}
              onClick={() => !full && onSelect(letter)}
              disabled={full}>
              {full && (
                <div className="di-team-filled-overlay">
                  <span className="di-team-filled-label">TEAM FILLED</span>
                </div>
              )}
              <div className="di-team-letter">TEAM {letter}</div>
              <div className="di-team-count">
                {count === null ? `- / ${teamCap(level)}` : `${count} / ${teamCap(level)}`}
                <span className="di-team-count-label">spots filled</span>
              </div>

              {level === 'beg' ? (
                <div className="di-team-spots">
                  {Array.from({ length: teamCap(level) }, (_, i) => (
                    <div key={i} className={`di-team-spot-dot${count !== null && i < count ? ' taken' : ''}`} />
                  ))}
                </div>
              ) : (
                <div className="di-team-pos-list">
                  {Object.entries(POS_CAPS).map(([pos, cap]) => {
                    const filled = posCounts ? (posCounts[pos] || 0) : null;
                    const posFull = filled !== null && filled >= cap;
                    return (
                      <div key={pos} className={`di-team-pos-chip${posFull ? ' full' : ''}`}>
                        <span className="di-tpc-pos">{pos}</span>
                        <span className="di-tpc-count">{filled === null ? '-' : `${filled}/${cap}`}</span>
                      </div>
                    );
                  })}
                  {(() => {
                    const libCount = posCounts ? (posCounts['L'] || 0) : null;
                    return (
                      <div className={`di-team-pos-chip${libCount !== null && libCount > 0 ? ' taken' : ''}`}>
                        <span className="di-tpc-pos">L</span>
                        <span className="di-tpc-count">{libCount === null ? '-' : libCount}</span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Position step ---------- */
function PositionStep({ level, session, claims, setClaims, claimedKey, setClaimedKey, rotation, setRotation, userAvatar, avatarMap }) {
  const [hoveredRole, setHoveredRole] = useState(null);
  const [tick, setTick] = useState(0);

  const handleClaim = (role) => {
    if (level === 'beg') {
      const already = claims.find(c => c.isYou);
      if (already && already.slot === role) return;
      const next = claims.filter(c => !c.isYou);
      const exists = next.find(c => c.slot === role);
      if (exists) return;
      next.push({ slot: role, isYou: true });
      setClaims(next);
      setClaimedKey(role);
      setTick(t => t + 1);
      return;
    }
    const meta = ROLE_META[role];
    const arr = claims[role] || [];
    const youIn = arr.find(c => c.isYou);
    const old = findYourClaim(claims);
    if (youIn) return;
    if (arr.length >= meta.capacity) return;
    const next = {
      S: [...claims.S], OH: [...claims.OH], MB: [...claims.MB],
      OPP: [...claims.OPP], L: [...claims.L],
    };
    if (old) {
      next[old.role] = next[old.role].filter(c => !c.isYou);
    }
    next[role] = [...next[role], { isYou: true }];
    setClaims(next);
    setClaimedKey(role);
    setTick(t => t + 1);
  };

  const yourClaim = level === 'beg'
    ? claims.find(c => c.isYou)
    : findYourClaim(claims);

  return (
    <div className="di-step">
      <div className="di-eyebrow">{LEVELS.find(L => L.id === level).tag} · {session.month}</div>
      <h1 className="di-title">CLAIM YOUR <em>SPOT</em></h1>
      <p className="di-sub">
        {level === 'beg'
          ? 'No assigned positions. Just grab any open spot on court. You can move around freely during play.'
          : 'Pick where you want to play. Setter, two outsides, two middles, opposite, and a libero. Tokens drop in when you claim.'}
      </p>

      <div className="di-position-step">
        <div className="di-court-frame">
          <div className="di-court-toolbar">
            <h3>HALF COURT <span>· {level === 'beg' ? 'OPEN PLAY' : `ROTATION ${rotation + 1} / 6`}</span></h3>
            {level !== 'beg' && (
              <div className="di-rotation-control" role="group" aria-label="Rotation">
                <button onClick={() => setRotation((rotation + 5) % 6)} aria-label="Previous rotation">‹</button>
                <div className="di-rotation-label">ROT {rotation + 1}/6</div>
                <button onClick={() => setRotation((rotation + 1) % 6)} aria-label="Next rotation">›</button>
              </div>
            )}
          </div>

          <Court
            level={level}
            rotation={rotation}
            claims={claims}
            hoveredRole={hoveredRole}
            onHoverRole={setHoveredRole}
            onClaim={handleClaim}
            lastClaimedTick={tick}
            userAvatar={userAvatar}
            avatarMap={avatarMap || {}}
          />

          <div className="di-court-legend">
            <div className="item"><span className="swatch open"></span>Open</div>
            <div className="item"><span className="swatch taken"></span>Claimed</div>
            <div className="item"><span className="swatch you"></span>Your spot</div>
          </div>
        </div>

        <div className="di-side-rail">
          <div className="di-rail-card">
            <h4>{level === 'beg' ? 'OPEN SPOTS' : 'POSITIONS'}</h4>
            {level === 'beg' ? (
              <div className="di-pos-list">
                {[1,2,3,4,5,6].map(n => {
                  const slot = `BEG${n}`;
                  const claim = claims.find(c => c.slot === slot);
                  const youHere = claim && claim.isYou;
                  const taken = !!claim && !youHere;
                  return (
                    <button key={n}
                      className={`di-pos-row ${hoveredRole === slot ? 'highlighted' : ''} ${taken ? 'full' : ''} ${youHere ? 'highlighted' : ''}`}
                      onMouseEnter={() => setHoveredRole(slot)}
                      onMouseLeave={() => setHoveredRole(null)}
                      onClick={() => !taken && handleClaim(slot)}
                      disabled={taken}>
                      <span className="badge">{n}</span>
                      <span>
                        <span className="rname">Spot {n}</span>
                        <span className="rsub">{youHere ? 'You' : taken ? 'Taken' : 'Available'}</span>
                      </span>
                      <span className="rcap">{taken ? 'X' : '·'}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="di-pos-list">
                {ROLE_ORDER.map(r => {
                  const meta = ROLE_META[r];
                  const arr = claims[r] || [];
                  const yourSlot = arr.find(c => c.isYou);
                  const full = arr.length >= meta.capacity;
                  return (
                    <button key={r}
                      className={`di-pos-row ${hoveredRole === r ? 'highlighted' : ''} ${yourSlot ? 'highlighted' : full ? 'full' : arr.length > 0 ? 'taken' : ''}`}
                      onMouseEnter={() => setHoveredRole(r)}
                      onMouseLeave={() => setHoveredRole(null)}
                      onClick={() => (!full || yourSlot) && handleClaim(r)}
                      disabled={full && !yourSlot}>
                      <span className="badge">{meta.abbr}</span>
                      <span>
                        <span className="rname">{meta.name}</span>
                        <span className="rsub">{meta.desc}</span>
                      </span>
                      <span className="rcap">{arr.length}/{meta.capacity}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="di-rail-card" style={{padding:'16px 20px'}}>
            <h4 style={{margin:0, fontSize:14, letterSpacing:'0.1em'}}>YOUR SELECTION</h4>
            <div style={{marginTop:10, fontSize:13, color: yourClaim ? 'white' : 'var(--di-muted)'}}>
              {!yourClaim && (
                <div className="di-nothing-selected">Hover the court or list to preview · click to claim</div>
              )}
              {yourClaim && level === 'beg' && (
                <div>
                  <div style={{fontFamily:'Bebas Neue', fontSize:30, lineHeight:1, marginTop:6}}>
                    SPOT {yourClaim.slot.replace('BEG','')}
                  </div>
                  <div style={{fontSize:12, color:'var(--di-muted)', marginTop:4}}>Open play, move freely</div>
                </div>
              )}
              {yourClaim && level !== 'beg' && (
                <div>
                  <div style={{fontFamily:'Bebas Neue', fontSize:30, lineHeight:1, marginTop:6}}>
                    {ROLE_META[yourClaim.role].name.toUpperCase()}
                  </div>
                  <div style={{fontSize:12, color:'var(--di-muted)', marginTop:4}}>{ROLE_META[yourClaim.role].desc}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Confirm step ---------- */
function ConfirmStep({ level, session, teamLetter, claims, onEdit }) {
  const levelMeta = LEVELS.find(L => L.id === level);
  let positionLabel = '';
  if (level === 'beg') {
    const c = claims.find(x => x.isYou);
    positionLabel = c ? `Spot ${c.slot.replace('BEG','')}` : '';
  } else {
    const c = findYourClaim(claims);
    positionLabel = c ? ROLE_META[c.role].name : '';
  }

  return (
    <div className="di-step">
      <div className="di-eyebrow">Almost done</div>
      <h1 className="di-title">REVIEW &amp; <em>LOCK IT IN</em></h1>
      <p className="di-sub">Pay-on-arrival, or use your drop-in pack. We'll text you the address an hour before.</p>

      <div className="di-confirm-wrap">
        <div className="di-summary-card">
          <div className="di-summary-row">
            <div className="slabel">Level</div>
            <div className="svalue">
              {levelMeta.name}
              <small>{levelMeta.duration} · {levelMeta.perGame}</small>
            </div>
          </div>
          <div className="di-summary-row">
            <div className="slabel">Session</div>
            <div className="svalue">
              {session.month}
              <small>{session.time} · {session.location}</small>
            </div>
          </div>
          {teamLetter && (
            <div className="di-summary-row">
              <div className="slabel">Team</div>
              <div className="svalue">Team {teamLetter}</div>
            </div>
          )}
          <div className="di-summary-row">
            <div className="slabel">Position</div>
            <div className="svalue">
              {positionLabel.toUpperCase()}
              <small>{level === 'beg' ? 'Open play' : 'Locked in for the full session'}</small>
            </div>
          </div>
          <div className="di-summary-row">
            <div className="slabel">Cost</div>
            <div className="svalue">
              ${session.price_cents ? (session.price_cents / 100).toFixed(0) : '12'}
              <small>Paid securely via Stripe at checkout</small>
            </div>
          </div>
        </div>

        <div style={{
          marginTop: 14,
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 11, color: 'var(--di-muted)', letterSpacing: '0.04em',
        }}>
          <InfoIcon
            title="Cancellation Policy"
            body="Drop-in registrations must be cancelled at least 24 hours before the session start time to receive a full refund (minus processing fees). After that window, no refunds are issued."
          />
          <span>Policy info</span>
        </div>

        <div style={{marginTop: 24, display:'flex', gap:12, flexWrap:'wrap'}}>
          <button className="di-btn ghost" onClick={onEdit}>← EDIT SELECTION</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- DropInFlow root ---------- */
export default function DropInFlow({ onClose, onMyEvents, userAvatar }) {
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { addItem } = useCart();
  const [savedToCart, setSavedToCart] = useState(false);
  const [step, setStep] = useState('level');
  const [level, setLevel] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [teamLetter, setTeamLetter] = useState(null);
  const [claims, setClaims] = useState(null);
  const [claimedKey, setClaimedKey] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [avatarMap, setAvatarMap] = useState({});
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [confNum] = useState(null);
  const [pendingLevel, setPendingLevel] = useState(null);
  const [skip51Modal, setSkip51Modal] = useState(() => {
    try { return localStorage.getItem('ovr-skip-5-1-modal') === '1'; }
    catch (e) { return false; }
  });
  const [dbSessions, setDbSessions] = useState([]);
  const [teamRefreshKey, setTeamRefreshKey] = useState(0);

  useEffect(() => {
    supabase.from('drop_in_sessions').select('*').eq('status', 'open')
      .order('starts_at').then(({ data }) => setDbSessions(data || []));
  }, []);

  const session = dbSessions.find(s => s.id === sessionId);

  const advance = (to) => {
    setStep(to);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // eslint-disable-next-line no-unused-vars
  const showToast = (msg) => {
    setToast(msg);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2200);
  };
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  let canContinue = false;
  if (step === 'level') { canContinue = !!level; }
  if (step === 'session') { canContinue = !!sessionId; }
  if (step === 'team') { canContinue = !!teamLetter; }
  if (step === 'position') {
    canContinue = level === 'beg'
      ? !!(claims && claims.find(c => c.isYou))
      : !!(claims && findYourClaim(claims));
  }

  const handleContinue = async () => {
    if (step === 'level') {
      advance('session');
    } else if (step === 'session') {
      setTeamLetter(null);
      advance('team');
    } else if (step === 'team') {
      // Fetch live DB claims for the selected team (include user_id for avatars)
      const { data: rows } = await supabase
        .from('drop_in_registrations')
        .select('position, user_id')
        .eq('session_id', session.id)
        .eq('team_letter', teamLetter)
        .not('payment_status', 'in', '(admin_removed,refunded,user_cancelled)');
      // Pass null so existing registrations are shown as taken (not isYou),
      // preventing the "claim shift" bug when the user clicks a position.
      const builtClaims = buildClaimsFromRows(level, rows || [], null);
      setClaims(builtClaims);
      setClaimedKey(null);
      setRotation(0);

      // Batch-fetch avatars for everyone on this team
      const uids = (rows || []).map(r => r.user_id).filter(Boolean);
      if (uids.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, avatar_url')
          .in('id', uids);
        const map = {};
        (profiles || []).forEach(p => { if (p.avatar_url) map[p.id] = p.avatar_url; });
        setAvatarMap(map);
      } else {
        setAvatarMap({});
      }

      advance('position');
    } else if (step === 'position') {
      advance('confirm');
    }
  };

  const handleBack = () => {
    if (step === 'done') { onClose && onClose(); }
    else if (step === 'level') { onClose && onClose(); }
    else if (step === 'session') advance('level');
    else if (step === 'team') advance('session');
    else if (step === 'position') { setTeamRefreshKey(k => k + 1); advance('team'); }
    else if (step === 'confirm') advance('position');
  };

  return (
    <div className="di-shell">
      <nav className="di-nav">
        <div className="di-nav-logo" onClick={onClose} role="button" tabIndex={0}>
          <img
            src={'/ovr-logo.png'}
            alt="OVR"
          />
          <span className="di-nav-title">DROP-INS</span>
        </div>
      </nav>

      <StepBar step={step} />

      {step === 'level' && (
        <LevelStep
          value={level}
          onRequestLevel={(id) => {
            if (id === 'beg') { setLevel(id); }
            else if (skip51Modal) { setLevel(id); }
            else { setPendingLevel(id); }
          }}
        />
      )}
      {step === 'session' && (
        <SessionStep level={level} value={sessionId} onChange={setSessionId} allSessions={dbSessions} />
      )}
      {step === 'team' && session && (
        <TeamStep
          level={level}
          session={session}
          value={teamLetter}
          onSelect={setTeamLetter}
          refreshKey={teamRefreshKey}
        />
      )}
      {step === 'position' && session && claims && (
        <PositionStep
          level={level}
          session={session}
          claims={claims}
          setClaims={setClaims}
          claimedKey={claimedKey}
          setClaimedKey={setClaimedKey}
          rotation={rotation}
          setRotation={setRotation}
          userAvatar={userAvatar}
          avatarMap={avatarMap}
        />
      )}
      {step === 'confirm' && session && claims && (
        <ConfirmStep
          level={level}
          session={session}
          teamLetter={teamLetter}
          claims={claims}
          onEdit={() => advance('position')}
        />
      )}

      {(
      <div className="di-footer">
        <div style={{display:'flex', gap:10, alignItems:'center'}}>
          <button className="di-btn ghost" onClick={handleBack}>← BACK</button>
          <div className="di-footer-summary">
            {level && <span>LVL · <b>{LEVELS.find(L => L.id === level)?.name}</b></span>}
            {session && <span> &nbsp;·&nbsp; SESSION · <b>{session.month}</b></span>}
            {teamLetter && <span> &nbsp;·&nbsp; TEAM · <b>{teamLetter}</b></span>}
            {claims && (() => {
              const yc = level === 'beg' ? claims.find(c => c.isYou) : findYourClaim(claims);
              if (!yc) return null;
              const txt = level === 'beg'
                ? `Spot ${yc.slot.replace('BEG','')}`
                : ROLE_META[yc.role].name;
              return <span> &nbsp;·&nbsp; POS · <b>{txt}</b></span>;
            })()}
          </div>
        </div>
        <div style={{display:'flex', gap:10, alignItems:'center'}}>
          {step !== 'confirm' && (
            <button className="di-btn primary" disabled={!canContinue} onClick={handleContinue}>
              CONTINUE →
            </button>
          )}
          {step === 'confirm' && (
            <>
              <button
                className="di-btn primary"
                type="button"
                disabled={savedToCart}
                onClick={() => {
                  let positionLabel = '';
                  let positionCode = '';
                  if (level === 'beg') {
                    const c = claims.find(x => x.isYou);
                    positionLabel = c ? `Spot ${c.slot.replace('BEG', '')}` : '';
                    positionCode = c ? c.slot : ''; // e.g. "BEG1"
                  } else {
                    const c = findYourClaim(claims);
                    positionLabel = c ? ROLE_META[c.role].name : '';
                    positionCode = c ? c.role : ''; // e.g. "S", "OH", "MB", "OPP", "L"
                  }
                  const levelMeta = LEVELS.find(L => L.id === level);
                  addItem({
                    type: 'dropin',
                    label: `${levelMeta?.name || level.toUpperCase()} DROP-IN`,
                    sublabel: `${session?.month || ''} · Team ${teamLetter} · ${positionLabel}`,
                    fee: session?.price_cents ? `$${(session.price_cents / 100).toFixed(0)}` : '$12',
                    level,
                    sessionId: session?.id,
                    sessionLabel: session?.month || (session?.starts_at ? new Date(session.starts_at).toLocaleDateString('en-CA', { month: 'long', day: 'numeric' }) : ''),
                    sessionTime: session?.time || (session?.starts_at ? new Date(session.starts_at).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', hour12: true }) : ''),
                    sessionLocation: session?.location,
                    teamLetter,
                    position: positionCode,
                    positionLabel,
                  });
                  setSavedToCart(true);
                  onClose && onClose();
                  if (!user) openAuthModal(null, 'signup', 'checkout');
                }}
              >
                {savedToCart ? '✓ ADDED TO CART' : 'ADD TO CART & PAY →'}
              </button>
            </>
          )}
        </div>
      </div>
      )}

      {toast && <div className="di-toast">{toast}</div>}

      <FiveOneModal
        open={!!pendingLevel}
        levelId={pendingLevel}
        onConfirm={(dontAsk) => {
          if (dontAsk) {
            try { localStorage.setItem('ovr-skip-5-1-modal', '1'); } catch (e) {}
            setSkip51Modal(true);
          }
          setLevel(pendingLevel);
          setPendingLevel(null);
        }}
        onCancel={() => setPendingLevel(null)}
      />
    </div>
  );
}
